/**
 * Data Normalization Utilities for Submission Display
 * 
 * These functions normalize various data formats from wizard submissions
 * to ensure consistent display in the admin interface.
 */

/**
 * Convert various value types to array
 */
export const toArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'object') return Object.values(value).filter(Boolean);
    if (typeof value === 'string') return value.split(/[\n,;]+/).map((item) => item.trim()).filter(Boolean);
    return [];
};

/**
 * Normalize education information from various formats
 */
export const normalizeEducationInfo = (educationInfo) => {
    if (!educationInfo) return null;
    const info = typeof educationInfo === 'object' && !Array.isArray(educationInfo)
        ? { ...educationInfo }
        : {};

    const additionalQualifications = toArray(info.additionalQualifications || info.additional_qualifications);

    const highestEducation = info.highestEducation || info.highest_degree || info.degree || '';
    const fieldOfStudy = info.fieldOfStudy || info.field_of_study || info.major || '';
    const university = info.university || info.institution || '';
    const graduationYear = info.graduationYear || info.year || '';
    const grade = info.grade || info.cgpa || '';

    return {
        ...info,
        highestEducation,
        fieldOfStudy,
        university,
        graduationYear,
        grade,
        additionalQualifications,
        notes: info.notes || ''
    };
};

/**
 * Normalize experience information from various formats
 */
export const normalizeExperienceInfo = (experienceInfo) => {
    if (!experienceInfo) return [];
    if (Array.isArray(experienceInfo)) {
        return experienceInfo.map((exp) => ({
            ...exp,
            jobTitle: exp.jobTitle || exp.title || exp.role || '',
            company: exp.company || exp.organization || '',
            duration: exp.duration || exp.years || '',
            description: exp.description || '',
        }));
    }

    if (typeof experienceInfo === 'string') {
        return experienceInfo
            .split(/\s*,\s*/)
            .map((entry) => entry.trim())
            .filter(Boolean)
            .map((entry) => {
                const [rolePart, durationPart = ''] = entry.split(' - ');
                const jobTitle = rolePart.replace(/\(.*?\)/g, '').trim();
                const companyMatch = rolePart.match(/\(([^)]+)\)/);
                return {
                    jobTitle: jobTitle || entry,
                    company: companyMatch ? companyMatch[1].trim() : '',
                    duration: durationPart.trim(),
                    description: entry,
                };
            });
    }

    if (typeof experienceInfo === 'object') {
        return Object.values(experienceInfo).map((entry) => ({
            jobTitle: entry?.jobTitle || entry?.title || '',
            company: entry?.company || entry?.organization || '',
            duration: entry?.duration || '',
            description: entry?.description || '',
        }));
    }

    return [];
};

/**
 * Normalize language information from various formats
 */
export const normalizeLanguageInfo = (languageInfo) => {
    if (!languageInfo) return null;
    const info = { ...languageInfo };
    const languages = toArray(info.languages);
    info.languages = languages;

    if (!info.otherLanguages && languages.length > 0) {
        info.otherLanguages = languages
            .filter((lang) => lang && lang.toLowerCase() !== 'english')
            .join(', ');
    }

    if (!info.englishSpeaking && languages.some((lang) => lang?.toLowerCase() === 'english')) {
        info.englishSpeaking = 'Fluent';
    }

    info.ieltsScore = info.ieltsScore || info.ielts_score || null;
    if (info.ieltsScore && !info.ielts_score) {
        info.ielts_score = info.ieltsScore;
    }

    return info;
};

/**
 * Normalize financial information from various formats
 */
export const normalizeFinancialInfo = (financialInfo) => {
    if (!financialInfo) return null;
    const info = { ...financialInfo };
    info.currency = info.currency || 'LKR';
    const fundsRaw = info.availableFunds ?? info.available_funds;
    if (fundsRaw !== undefined && fundsRaw !== null && fundsRaw !== '') {
        const numericFunds = Number(fundsRaw);
        info.availableFunds = Number.isFinite(numericFunds) ? numericFunds : fundsRaw;
    } else {
        info.availableFunds = '';
    }
    info.educationBudget = info.educationBudget || info.budget_range || '';
    info.sufficientForBlockedAccount = info.sufficientForBlockedAccount ?? info.sufficient_for_blocked_account ?? info.blockedAccount ?? null;
    if (info.sufficientForBlockedAccount !== null && info.sufficientForBlockedAccount !== undefined) {
        info.blockedAccount = info.sufficientForBlockedAccount ? 'Yes' : 'No';
    }
    return info;
};

/**
 * Normalize CV data from various formats
 */
export const normalizeCvData = (cvData) => {
    if (!cvData) return null;
    if (typeof cvData === 'string') {
        return { summary: cvData };
    }
    return { ...cvData };
};

/**
 * Normalize entire submission for display
 */
export const normalizeSubmissionForDisplay = (submission) => {
    if (!submission) return submission;
    const normalized = { ...submission };

    normalized.country_selection = submission.country_selection ? { ...submission.country_selection } : submission.country_selection;
    normalized.personal_info = submission.personal_info ? { ...submission.personal_info } : submission.personal_info;
    normalized.education_info = normalizeEducationInfo(submission.education_info);
    normalized.experience_info = normalizeExperienceInfo(submission.experience_info);
    normalized.language_info = normalizeLanguageInfo(submission.language_info);
    normalized.financial_info = normalizeFinancialInfo(submission.financial_info);
    normalized.cv_data = normalizeCvData(submission.cv_data);

    if (!normalized.selected_pathway && normalized.country_selection?.selectedPathway) {
        normalized.selected_pathway = normalized.country_selection.selectedPathway;
    }

    return normalized;
};

/**
 * Format currency value with appropriate symbol and formatting
 */
export const formatCurrencyValue = (amount, currency = 'USD') => {
    if (amount === null || amount === undefined || amount === '') {
        return 'Not specified';
    }

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) {
        return String(amount);
    }

    const currencySymbols = {
        USD: '$',
        EUR: '€',
        GBP: '£',
        LKR: 'Rs.',
        INR: '₹',
        AUD: 'A$',
        CAD: 'C$',
    };

    const symbol = currencySymbols[currency] || currency + ' ';
    return `${symbol}${numericAmount.toLocaleString()}`;
};

/**
 * Normalize timeline milestones array
 */
export const normalizeTimelineMilestones = (milestones = []) => {
    return milestones.map((milestone, index) => ({
        id: milestone.id || `milestone-${index}`,
        name: milestone.name || milestone.title || `Step ${index + 1}`,
        description: milestone.description || '',
        dueDate: milestone.dueDate || milestone.due_date || null,
        status: milestone.status || 'pending',
        priority: milestone.priority || 'medium',
        notes: milestone.notes || '',
        completed: milestone.completed || milestone.status === 'completed',
        completedAt: milestone.completedAt || milestone.completed_at || null
    }));
};

export default {
    toArray,
    normalizeEducationInfo,
    normalizeExperienceInfo,
    normalizeLanguageInfo,
    normalizeFinancialInfo,
    normalizeCvData,
    normalizeSubmissionForDisplay,
    formatCurrencyValue,
    normalizeTimelineMilestones
};
